// SolarInstallationCCTVService.js - Detailed CCTV Installation service component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a detailed CCTV Installation service section
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the service section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The CCTV service section elements
 */
export const createSolarInstallationCCTVServiceElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const cctvSectionClass = 'cctv-service-section';
  const cctvContainerClass = 'cctv-service-container';
  const cctvHeaderClass = 'cctv-service-header';
  const cctvTitleClass = 'cctv-service-title';
  const cctvSubtitleClass = 'cctv-service-subtitle';
  const cctvContentClass = 'cctv-service-content';
  const cctvHeroClass = 'cctv-service-hero';
  const cctvHeroImageClass = 'cctv-service-hero-image';
  const cctvHeroTextClass = 'cctv-service-hero-text';
  const cctvFeaturesClass = 'cctv-service-features';
  const cctvFeatureCardClass = 'cctv-service-feature-card';
  const cctvFeatureIconClass = 'cctv-service-feature-icon';
  const cctvFeatureTitleClass = 'cctv-service-feature-title';
  const cctvFeatureDescClass = 'cctv-service-feature-desc';
  const cctvPackagesClass = 'cctv-service-packages';
  const cctvPackageCardClass = 'cctv-service-package-card';
  const cctvPackageTitleClass = 'cctv-service-package-title';
  const cctvPackagePriceClass = 'cctv-service-package-price';
  const cctvPackageListClass = 'cctv-service-package-list';
  const cctvPackageItemClass = 'cctv-service-package-item';
  const cctvCtaClass = 'cctv-service-cta';
  const cctvCtaButtonClass = 'cctv-service-cta-button';
  
  // Desktop Styles - Solar themed with deep blue background
  const cctvSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#0f2341',
    background: `
      radial-gradient(circle at 20% 80%, rgba(74, 222, 128, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(34, 197, 94, 0.05) 0%, transparent 50%),
      linear-gradient(135deg, #0f2341 0%, #1e3a8a 50%, #0f2341 100%)
    `,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const cctvContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '60px'
  };

  const cctvHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '20px',
    width: '100%'
  };

  const cctvTitleStyles = {
    fontSize: '48px',
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif',
    letterSpacing: '-0.02em',
    lineHeight: '1.2'
  };

  const cctvSubtitleStyles = {
    fontSize: '20px',
    color: '#e2e8f0',
    lineHeight: '1.6',
    marginBottom: '30px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400',
    maxWidth: '700px'
  };

  const cctvContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: '80px'
  };

  const cctvHeroStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '50px',
    width: '100%'
  };

  const cctvHeroImageStyles = {
    flex: '1',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
  };

  const cctvHeroTextStyles = {
    flex: '1',
    color: '#ffffff',
    fontSize: '18px',
    lineHeight: '1.8',
    fontFamily: '"Inter", sans-serif'
  };

  const cctvFeaturesStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '30px',
    width: '100%'
  };

  const cctvFeatureCardStyles = {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '16px',
    padding: '30px',
    border: '1px solid rgba(74, 222, 128, 0.2)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease'
  };

  const cctvFeatureIconStyles = {
    fontSize: '48px',
    marginBottom: '20px',
    display: 'block'
  };

  const cctvFeatureTitleStyles = {
    fontSize: '24px',
    fontWeight: '600',
    color: '#4ade80',
    marginBottom: '15px',
    fontFamily: '"Inter", sans-serif'
  };

  const cctvFeatureDescStyles = {
    fontSize: '16px',
    color: '#e2e8f0',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif'
  };

  const cctvPackagesStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '30px',
    width: '100%'
  };

  const cctvPackageCardStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '40px',
    border: '2px solid #4ade80',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease'
  };

  const cctvPackageTitleStyles = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#0f2341',
    marginBottom: '10px',
    fontFamily: '"Inter", sans-serif'
  };

  const cctvPackagePriceStyles = {
    fontSize: '36px',
    fontWeight: '700',
    color: '#4ade80',
    marginBottom: '25px',
    fontFamily: '"Inter", sans-serif'
  };

  const cctvPackageListStyles = {
    listStyle: 'none',
    padding: '0',
    margin: '0'
  };

  const cctvPackageItemStyles = {
    fontSize: '16px',
    color: '#475569',
    marginBottom: '12px',
    paddingLeft: '25px',
    position: 'relative',
    fontFamily: '"Inter", sans-serif'
  };

  const cctvCtaStyles = {
    textAlign: 'center',
    width: '100%'
  };

  const cctvCtaButtonStyles = {
    padding: '18px 40px',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: '600',
    backgroundColor: '#4ade80',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: '"Inter", sans-serif',
    boxShadow: '0 4px 15px rgba(74, 222, 128, 0.3)'
  };

  // Mobile styles
  const mobileCctvHeroStyles = {
    flexDirection: 'column',
    gap: '30px'
  };

  const mobileCctvTitleStyles = {
    fontSize: '36px'
  };

  const mobileCctvPackageCardStyles = {
    padding: '30px 20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(cctvSectionClass, cctvSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvContainerClass, cctvContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvHeaderClass, cctvHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvTitleClass, cctvTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvSubtitleClass, cctvSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvContentClass, cctvContentStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvHeroClass, cctvHeroStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvHeroImageClass, cctvHeroImageStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvHeroTextClass, cctvHeroTextStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvFeaturesClass, cctvFeaturesStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvFeatureCardClass, cctvFeatureCardStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvFeatureIconClass, cctvFeatureIconStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvFeatureTitleClass, cctvFeatureTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvFeatureDescClass, cctvFeatureDescStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvPackagesClass, cctvPackagesStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvPackageCardClass, cctvPackageCardStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvPackageTitleClass, cctvPackageTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvPackagePriceClass, cctvPackagePriceStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvPackageListClass, cctvPackageListStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvPackageItemClass, cctvPackageItemStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvCtaClass, cctvCtaStyles);
    newRegistry[DEVICES.DESKTOP].set(cctvCtaButtonClass, cctvCtaButtonStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(cctvHeroClass, mobileCctvHeroStyles);
    newRegistry[DEVICES.MOBILE].set(cctvTitleClass, mobileCctvTitleStyles);
    newRegistry[DEVICES.MOBILE].set(cctvPackageCardClass, mobileCctvPackageCardStyles);

    return newRegistry;
  });
  
  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    cctvSectionClass,
    cctvContainerClass,
    cctvHeaderClass,
    cctvTitleClass,
    cctvSubtitleClass,
    cctvContentClass,
    cctvHeroClass,
    cctvHeroImageClass,
    cctvHeroTextClass,
    cctvFeaturesClass,
    cctvFeatureCardClass,
    cctvFeatureIconClass,
    cctvFeatureTitleClass,
    cctvFeatureDescClass,
    cctvPackagesClass,
    cctvPackageCardClass,
    cctvPackageTitleClass,
    cctvPackagePriceClass,
    cctvPackageListClass,
    cctvPackageItemClass,
    cctvCtaClass,
    cctvCtaButtonClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = sectionId + 1;
  const headerId = sectionId + 2;
  const titleId = sectionId + 3;
  const subtitleId = sectionId + 4;
  const contentId = sectionId + 5;
  const heroId = sectionId + 6;
  const heroImageId = sectionId + 7;
  const heroTextId = sectionId + 8;
  const featuresId = sectionId + 9;
  const packagesId = sectionId + 20;
  const ctaId = sectionId + 30;
  const ctaButtonId = sectionId + 31;

  // Feature card IDs
  const feature1Id = sectionId + 10;
  const feature2Id = sectionId + 11;
  const feature3Id = sectionId + 12;
  const feature4Id = sectionId + 13;

  // Package card IDs
  const package1Id = sectionId + 21;
  const package2Id = sectionId + 22;
  const package3Id = sectionId + 23;

  const newElements = [
    // Main CCTV Service Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'CCTV Service Section',
      parentId: null,
      className: cctvSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'cctv-service-section',
        'data-component': 'section',
        'data-section-type': 'service',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'CCTV Service Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: cctvContainerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: {
        'data-element': 'cctv-service-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'CCTV Service Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: cctvHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'cctv-service-header'
      }
    },
    
    // Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h1',
      name: 'CCTV Service Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h1'),
      className: cctvTitleClass,
      styles: {},
      children: [],
      text: 'CCTV Installation & Repair Services',
      attributes: {
        'data-element': 'cctv-service-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'CCTV Service Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: cctvSubtitleClass,
      styles: {},
      children: [],
      text: 'Professional surveillance solutions for residential, commercial, and industrial properties. Complete CCTV system design, installation, maintenance, and 24/7 monitoring services.',
      attributes: {
        'data-element': 'cctv-service-subtitle'
      }
    },
    
    // Content Container
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'CCTV Service Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: cctvContentClass,
      styles: {},
      children: [heroId, featuresId, packagesId, ctaId],
      text: '',
      attributes: {
        'data-element': 'cctv-service-content'
      }
    },
    
    // Hero Section
    {
      id: heroId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'CCTV Service Hero',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: cctvHeroClass,
      styles: {},
      children: [heroImageId, heroTextId],
      text: '',
      attributes: {
        'data-element': 'cctv-service-hero'
      }
    },
    
    // Hero Image
    {
      id: heroImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'CCTV Service Hero Image',
      parentId: heroId,
      hierarchyName: getHierarchyName('image'),
      className: cctvHeroImageClass,
      styles: { width: '100%', height: 'auto' },
      src: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      children: [],
      text: '',
      attributes: {
        'data-element': 'cctv-service-hero-image',
        'alt': 'Professional CCTV camera installation'
      }
    },
    
    // Hero Text
    {
      id: heroTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'CCTV Service Hero Text',
      parentId: heroId,
      hierarchyName: getHierarchyName('div'),
      className: cctvHeroTextClass,
      styles: {},
      children: [],
      text: 'At JNT & Co., we provide comprehensive CCTV surveillance solutions that ensure the security and safety of your property. Our expert team designs and installs state-of-the-art camera systems tailored to your specific security needs. From high-definition IP cameras to advanced night vision systems, we offer complete surveillance coverage with remote monitoring capabilities. Our services include system design, professional installation, ongoing maintenance, and 24/7 technical support to keep your property secure around the clock.',
      attributes: {
        'data-element': 'cctv-service-hero-text'
      }
    }
  ];

  return {
    cctvServiceSection: newElements[0],
    elements: newElements
  };
}; 